import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { isFeatureEnabled } from '@michelin/central-provider';
import { V3_ROUTE_SEGMENTS } from 'V3Routes';

export type UIVersion = 'new' | 'legacy';

interface UIVersionContextValue {
  uiVersion: UIVersion;
  toggleUIVersion: () => void;
  isNewUI: boolean;
  featureEnabled: boolean;
}

const UIVersionContext = createContext<UIVersionContextValue>({
  uiVersion: 'legacy',
  toggleUIVersion: () => {},
  isNewUI: false,
  featureEnabled: false,
});

const STORAGE_KEY = 'cp-ui-version';

function isV3Route(): boolean {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  return pathSegments.some((segment) => V3_ROUTE_SEGMENTS.includes(segment));
}

export function UIVersionProvider({ children }: { children: React.ReactNode }) {
  const featureEnabled = isFeatureEnabled('msp3_new_interface');

  const [uiVersion, setUIVersion] = useState<UIVersion>(() => {
    // If the user navigated directly to a v3 route, use the new UI
    // regardless of what's in localStorage or whether feature flags have loaded
    if (isV3Route()) {
      try {
        localStorage.setItem(STORAGE_KEY, 'new');
      } catch {
        // ignore
      }
      return 'new';
    }
    if (!featureEnabled) return 'legacy';
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'new' ? 'new' : 'legacy';
    } catch {
      return 'legacy';
    }
  });

  const toggleUIVersion = useCallback(() => {
    if (!featureEnabled) return;
    setUIVersion((prev) => {
      const next = prev === 'legacy' ? 'new' : 'legacy';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, [featureEnabled]);

  const isNewUI = uiVersion === 'new' && (featureEnabled || isV3Route());

  const value = useMemo(
    () => ({
      uiVersion,
      toggleUIVersion,
      isNewUI,
      featureEnabled,
    }),
    [uiVersion, toggleUIVersion, isNewUI, featureEnabled],
  );

  return <UIVersionContext.Provider value={value}>{children}</UIVersionContext.Provider>;
}

export function useUIVersion() {
  return useContext(UIVersionContext);
}
